import React, { useContext, useEffect, useState } from 'react';
import Header from '../../layout/header/Header';
import Footer from '../../layout/footer/Footer';
import { Container, Card, Button, Badge, Alert, Row, Col, Form } from 'react-bootstrap';
import { GlobalContext } from '../../store/GlobalContext';
import axios from '../../api/axios';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { API_ASSET_BASE_URL } from '../../config/env';
import { getFallbackProductImage, resolveProductImage } from '../../data/productsApi';
import { useI18n } from '../../i18n/I18nContext';
import { getOrderStatusMeta, isDeliveredOrder, ORDER_STATUS_OPTIONS } from '../../utils/orderStatus';

type OrderRow = {
  _id: string;
  orderNumber: string;
  items?: Array<{
    _id: string;
    productId: string;
    productName: string;
    image?: string;
    quantity: number;
    price: number;
    subtotal?: number;
  }>;
  totalAmount: number;
  status: string;
  paymentStatus: 'paid' | 'unpaid' | 'pending';
  failureReason?: string;
  deliveryNote?: string;
  deliveryBoy?: {
    id?: string;
    name?: string;
    phoneNumber?: string;
  } | null;
  createdAt?: string;
};

type OrderTimelineEvent = {
  fromStatus?: string | null;
  toStatus: string;
  note?: string | null;
  actor?: string | null;
  createdAt: string;
};

type FavouriteRow = {
  productId: string;
  name: string;
  description?: string;
  price: number;
  discount?: number;
  images?: string[];
  ratings?: number;
  reviewsCount?: number;
};

type CustomerProfile = {
  _id?: string;
  name?: string;
  phone?: string;
  email?: string;
  createdAt?: string;
};

type NotificationRow = {
  _id: string;
  type: string;
  title: string;
  message: string;
  isRead: boolean;
  createdAt: string;
};

type ReturnRequestRow = {
  _id: string;
  orderId: string;
  orderNumber: string;
  status: 'requested' | 'approved' | 'rejected' | 'picked_up' | 'refunded' | 'closed';
  reason: string;
  note?: string | null;
  adminNote?: string | null;
  refundAmount?: number | null;
  createdAt?: string;
  updatedAt?: string;
};

function getPaymentStatusMeta(paymentStatus: string, orderStatus: string): { label: string; badge: string } {
  const normalized = String(paymentStatus || '').toLowerCase();
  if (normalized === 'paid') return { label: 'Paid', badge: 'success' };
  if (orderStatus === 'delivered_pending_payment' || normalized === 'pending') {
    return { label: 'Pending Payment', badge: 'warning' };
  }
  return { label: 'Unpaid', badge: 'secondary' };
}

const Account = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { state, logout, login } = useContext(GlobalContext);
  const { user } = state.auth;
  const { t } = useI18n();
  const token = state.auth.token;
  const roles: string[] = Array.isArray(user?.roles) ? (user?.roles as string[]) : [];
  const isCustomer = roles.includes('customer');

  const [profileLoading, setProfileLoading] = useState(false);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileError, setProfileError] = useState('');
  const [profileSuccess, setProfileSuccess] = useState('');
  const [customerProfile, setCustomerProfile] = useState<CustomerProfile | null>(null);
  const [profileForm, setProfileForm] = useState({
    username: '',
    email: '',
    firstName: '',
    lastName: '',
    customerName: '',
    phone: '',
  });

  const [orders, setOrders] = useState<OrderRow[]>([]);
  const [orderSearchInput, setOrderSearchInput] = useState('');
  const [orderSearch, setOrderSearch] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all');
  const [orderPaymentFilter, setOrderPaymentFilter] = useState<'all' | 'paid' | 'unpaid'>('all');
  const [orderSort, setOrderSort] = useState<'newest' | 'oldest' | 'totalAmount_desc' | 'totalAmount_asc'>('newest');
  const [favourites, setFavourites] = useState<FavouriteRow[]>([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingFavourites, setLoadingFavourites] = useState(false);
  const [orderError, setOrderError] = useState('');
  const [favouriteError, setFavouriteError] = useState('');
  const [expandedOrderId, setExpandedOrderId] = useState<string | null>(null);
  const [timelineLoadingId, setTimelineLoadingId] = useState<string | null>(null);
  const [timelinesByOrderId, setTimelinesByOrderId] = useState<Record<string, OrderTimelineEvent[]>>({});

  const [notifications, setNotifications] = useState<NotificationRow[]>([]);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [unreadNotifications, setUnreadNotifications] = useState(0);

  const [returnsList, setReturnsList] = useState<ReturnRequestRow[]>([]);
  const [loadingReturns, setLoadingReturns] = useState(false);
  const [returnsError, setReturnsError] = useState('');
  const [returnsSuccess, setReturnsSuccess] = useState('');

  const resolveSection = (): 'profile' | 'orders' | 'favourites' | 'notifications' | 'returns' => {
    if (location.pathname.endsWith('/orders')) return 'orders';
    if (location.pathname.endsWith('/favourites')) return 'favourites';
    if (location.pathname.endsWith('/notifications')) return 'notifications';
    if (location.pathname.endsWith('/returns')) return 'returns';
    return 'profile';
  };

  const activeSection = resolveSection();
  const placedOrder = searchParams.get('placed');

  useEffect(() => {
    const fetchProfile = async () => {
      if (!token) return;
      setProfileLoading(true);
      setProfileError('');
      try {
        const userResponse = await axios.get('/users/me', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const userData = userResponse?.data || {};

        let nextCustomerProfile: CustomerProfile | null = null;
        if (isCustomer) {
          try {
            const customerResponse = await axios.get('/customers/me', {
              headers: { Authorization: `Bearer ${token}` },
            });
            nextCustomerProfile = customerResponse?.data || null;
          } catch (customerErr: any) {
            const statusCode = customerErr?.response?.status;
            if (statusCode !== 404) {
              throw customerErr;
            }
          }
        }

        setCustomerProfile(nextCustomerProfile);
        setProfileForm({
          username: userData.username || '',
          email: userData.email || '',
          firstName: userData.firstName || '',
          lastName: userData.lastName || '',
          customerName: nextCustomerProfile?.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim(),
          phone: nextCustomerProfile?.phone || '',
        });
      } catch (err: any) {
        setProfileError(err?.response?.data?.message || 'Could not load profile details.');
      } finally {
        setProfileLoading(false);
      }
    };

    fetchProfile();
  }, [token, isCustomer]);

  useEffect(() => {
    const fetchMyOrders = async () => {
      if (!token) return;
      setLoadingOrders(true);
      setOrderError('');
      try {
        const response = await axios.get('/orders/my-orders', {
          headers: { Authorization: `Bearer ${token}` },
          params: {
            page: 1,
            limit: 20,
            sort: orderSort,
            search: orderSearch || undefined,
            status: orderStatusFilter === 'all' ? undefined : orderStatusFilter,
            paymentStatus: orderPaymentFilter === 'all' ? undefined : orderPaymentFilter,
          },
        });
        const list = Array.isArray(response.data?.orders)
          ? response.data.orders
          : Array.isArray(response.data)
            ? response.data
            : [];
        setOrders(list);
      } catch (err: any) {
        setOrderError(err?.response?.data?.message || 'Could not load order history.');
        setOrders([]);
      } finally {
        setLoadingOrders(false);
      }
    };

    fetchMyOrders();
  }, [token, orderPaymentFilter, orderSearch, orderSort, orderStatusFilter]);

  const toggleOrderTimeline = async (orderId: string) => {
    if (!token) return;

    if (expandedOrderId === orderId) {
      setExpandedOrderId(null);
      return;
    }

    setExpandedOrderId(orderId);
    if (timelinesByOrderId[orderId]) {
      return;
    }

    setTimelineLoadingId(orderId);
    try {
      const response = await axios.get(`/orders/${orderId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const timeline = Array.isArray(response?.data?.timeline) ? response.data.timeline : [];
      setTimelinesByOrderId((prev) => ({ ...prev, [orderId]: timeline }));
    } catch {
      setTimelinesByOrderId((prev) => ({ ...prev, [orderId]: [] }));
    } finally {
      setTimelineLoadingId(null);
    }
  };

  useEffect(() => {
    const fetchFavourites = async () => {
      if (!token) return;
      setLoadingFavourites(true);
      setFavouriteError('');
      try {
        const response = await axios.get('/favourites/my', {
          headers: { Authorization: `Bearer ${token}` },
        });
        const list = Array.isArray(response.data?.items) ? response.data.items : [];
        setFavourites(list);
      } catch (err: any) {
        setFavouriteError(err?.response?.data?.message || 'Could not load favourites.');
        setFavourites([]);
      } finally {
        setLoadingFavourites(false);
      }
    };

    fetchFavourites();
  }, [token]);

  useEffect(() => {
    const fetchNotifications = async () => {
      if (!token) return;
      setLoadingNotifications(true);
      setNotificationError('');
      try {
        const response = await axios.get('/notifications/my', {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, limit: 50 },
        });
        const list = Array.isArray(response.data?.notifications) ? response.data.notifications : [];
        setNotifications(list);
        setUnreadNotifications(Number(response.data?.unreadCount ?? 0));
      } catch (err: any) {
        setNotificationError(err?.response?.data?.message || 'Could not load notifications.');
        setNotifications([]);
      } finally {
        setLoadingNotifications(false);
      }
    };

    fetchNotifications();
  }, [token]);

  useEffect(() => {
    const fetchReturns = async () => {
      if (!token || !isCustomer) return;
      setLoadingReturns(true);
      setReturnsError('');
      try {
        const response = await axios.get('/returns/my', {
          headers: { Authorization: `Bearer ${token}` },
          params: { page: 1, limit: 100 },
        });
        const list = Array.isArray(response.data?.returns) ? response.data.returns : [];
        setReturnsList(list);
      } catch (err: any) {
        setReturnsError(err?.response?.data?.message || 'Could not load return requests.');
        setReturnsList([]);
      } finally {
        setLoadingReturns(false);
      }
    };

    fetchReturns();
  }, [token, isCustomer]);

  const requestReturnForOrder = async (order: OrderRow) => {
    if (!token) return;

    const reason = window.prompt('Enter return reason:');
    if (!reason || !reason.trim()) {
      return;
    }
    const note = window.prompt('Optional note for the return request:') || '';

    try {
      await axios.post(
        '/returns/my/request',
        {
          orderId: order._id,
          reason: reason.trim(),
          note: note.trim() || undefined,
        },
        { headers: { Authorization: `Bearer ${token}` } },
      );
      setReturnsSuccess(`Return request submitted for ${order.orderNumber}.`);

      const response = await axios.get('/returns/my', {
        headers: { Authorization: `Bearer ${token}` },
        params: { page: 1, limit: 100 },
      });
      const list = Array.isArray(response.data?.returns) ? response.data.returns : [];
      setReturnsList(list);
      setReturnsError('');
    } catch (err: any) {
      setReturnsError(err?.response?.data?.message || 'Could not submit return request.');
      setReturnsSuccess('');
    }
  };

  const markNotificationRead = async (notificationId: string) => {
    if (!token) return;
    try {
      await axios.put(`/notifications/my/${notificationId}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((item) => item._id === notificationId ? { ...item, isRead: true } : item));
      setUnreadNotifications((prev) => Math.max(prev - 1, 0));
    } catch (err: any) {
      setNotificationError(err?.response?.data?.message || 'Could not mark notification as read.');
    }
  };

  const markAllNotificationsRead = async () => {
    if (!token) return;
    try {
      await axios.put('/notifications/my/read-all', {}, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setNotifications((prev) => prev.map((item) => ({ ...item, isRead: true })));
      setUnreadNotifications(0);
      setNotificationError('');
    } catch (err: any) {
      setNotificationError(err?.response?.data?.message || 'Could not mark all notifications as read.');
    }
  };

  const removeFavourite = async (productId: string) => {
    if (!token) return;
    setFavouriteError('');
    try {
      const response = await axios.delete(`/favourites/my/items/${productId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const list = Array.isArray(response.data?.items) ? response.data.items : [];
      setFavourites(list);
    } catch (err: any) {
      setFavouriteError(err?.response?.data?.message || 'Could not remove favourite item.');
    }
  };

  const onProfileInputChange = (key: string, value: string) => {
    setProfileError('');
    setProfileSuccess('');
    setProfileForm((prev) => ({ ...prev, [key]: value }));
  };

  const saveProfile = async () => {
    if (!token) return;
    if (isCustomer && !profileForm.phone.trim()) {
      setProfileError('Phone is required for customer profile.');
      return;
    }

    setProfileSaving(true);
    setProfileError('');
    setProfileSuccess('');

    try {
      const userPayload = {
        username: profileForm.username.trim(),
        email: profileForm.email.trim(),
        firstName: profileForm.firstName.trim(),
        lastName: profileForm.lastName.trim(),
      };

      const userResponse = await axios.put('/users/me', userPayload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (isCustomer) {
        const customerPayload = {
          name: profileForm.customerName.trim() || `${profileForm.firstName} ${profileForm.lastName}`.trim(),
          phone: profileForm.phone.trim(),
          email: profileForm.email.trim(),
        };

        const customerResponse = await axios.put('/customers/me', customerPayload, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCustomerProfile(customerResponse?.data || null);
      }

      const nextUser = userResponse?.data || user;
      login(token, nextUser);
      setProfileSuccess('Profile updated successfully.');
    } catch (err: any) {
      setProfileError(err?.response?.data?.message || 'Could not update profile.');
    } finally {
      setProfileSaving(false);
    }
  };

  return (
    <>
      <Header />
      <Container className="my-5" style={{ minHeight: "27vh" }}>
        <div className='d-flex justify-content-between  mb-4'>
          <h2>Account</h2>
          <div>
            <Button variant="outline-dark" onClick={logout}>
              Logout
            </Button>
          </div>
        </div>
        <Card className="mb-4">
          <Card.Body>
            <Row>
              <Col md={3} className="mb-3 mb-md-0">
                <div className="border rounded p-2">
                  <Button
                    variant={activeSection === 'profile' ? 'dark' : 'outline-dark'}
                    className="w-100 text-start mb-2"
                    onClick={() => navigate('/account/profile')}
                  >
                    Profile
                  </Button>
                  <Button
                    variant={activeSection === 'orders' ? 'dark' : 'outline-dark'}
                    className="w-100 text-start mb-2"
                    onClick={() => navigate('/account/orders')}
                  >
                    Orders
                  </Button>
                  <Button
                    variant={activeSection === 'favourites' ? 'dark' : 'outline-dark'}
                    className="w-100 text-start"
                    onClick={() => navigate('/account/favourites')}
                  >
                    Favourites
                  </Button>
                  {token && (
                    <Button
                      variant={activeSection === 'notifications' ? 'dark' : 'outline-dark'}
                      className="w-100 text-start mt-2"
                      onClick={() => navigate('/account/notifications')}
                    >
                      {t('notifications_title')} {unreadNotifications > 0 ? `(${unreadNotifications})` : ''}
                    </Button>
                  )}
                  {isCustomer && (
                    <Button
                      variant={activeSection === 'returns' ? 'dark' : 'outline-dark'}
                      className="w-100 text-start mt-2"
                      onClick={() => navigate('/account/returns')}
                    >
                      Returns
                    </Button>
                  )}
                </div>
              </Col>

              <Col md={9}>
                {activeSection === 'profile' && (
                  <div>
                    <Card.Title>Profile</Card.Title>
                    {profileError && <Alert variant="danger">{profileError}</Alert>}
                    {profileSuccess && <Alert variant="success">{profileSuccess}</Alert>}

                    {profileLoading ? (
                      <Card.Text>Loading profile...</Card.Text>
                    ) : (
                      <Form>
                        <Row>
                          <Col md={6} className="mb-3">
                            <Form.Label>Username</Form.Label>
                            <Form.Control
                              value={profileForm.username}
                              onChange={(e) => onProfileInputChange('username', e.target.value)}
                            />
                          </Col>
                          <Col md={6} className="mb-3">
                            <Form.Label>Email</Form.Label>
                            <Form.Control
                              type="email"
                              value={profileForm.email}
                              onChange={(e) => onProfileInputChange('email', e.target.value)}
                            />
                          </Col>
                          <Col md={6} className="mb-3">
                            <Form.Label>First Name</Form.Label>
                            <Form.Control
                              value={profileForm.firstName}
                              onChange={(e) => onProfileInputChange('firstName', e.target.value)}
                            />
                          </Col>
                          <Col md={6} className="mb-3">
                            <Form.Label>Last Name</Form.Label>
                            <Form.Control
                              value={profileForm.lastName}
                              onChange={(e) => onProfileInputChange('lastName', e.target.value)}
                            />
                          </Col>

                          {isCustomer && (
                            <>
                              <Col md={6} className="mb-3">
                                <Form.Label>Customer Display Name</Form.Label>
                                <Form.Control
                                  value={profileForm.customerName}
                                  onChange={(e) => onProfileInputChange('customerName', e.target.value)}
                                  placeholder="How your name appears on orders"
                                />
                              </Col>
                              <Col md={6} className="mb-3">
                                <Form.Label>Phone</Form.Label>
                                <Form.Control
                                  value={profileForm.phone}
                                  onChange={(e) => onProfileInputChange('phone', e.target.value)}
                                  placeholder="03xxxxxxxxx or +923xxxxxxxxx"
                                />
                              </Col>
                            </>
                          )}
                        </Row>

                        <div className="d-flex gap-2">
                          <Button variant="dark" onClick={saveProfile} disabled={profileSaving}>
                            {profileSaving ? 'Saving...' : 'Save Profile'}
                          </Button>
                          {isCustomer && (
                            <Button variant="outline-dark" onClick={() => navigate('/checkout')}>
                              Open Cart & Checkout
                            </Button>
                          )}
                        </div>
                        {isCustomer && customerProfile?.createdAt ? (
                          <small className="text-muted d-block mt-2">
                            Profile linked since {new Date(String(customerProfile.createdAt)).toLocaleDateString()}
                          </small>
                        ) : null}
                      </Form>
                    )}
                  </div>
                )}

                {activeSection === 'orders' && (
                  <div>
                    <Card.Title className="mb-3">Order History</Card.Title>
                    {placedOrder && <Alert variant="success">Order placed successfully ({placedOrder}).</Alert>}
                    {orderError && <Alert variant="danger">{orderError}</Alert>}
                    <div className="d-flex flex-wrap gap-2 mb-3">
                      <Form
                        className="d-flex gap-2 flex-grow-1"
                        onSubmit={(event) => {
                          event.preventDefault();
                          setOrderSearch(orderSearchInput.trim());
                        }}
                      >
                        <Form.Control
                          placeholder="Search order number"
                          value={orderSearchInput}
                          onChange={(event) => setOrderSearchInput(event.target.value)}
                        />
                        <Button type="submit" variant="outline-dark">Search</Button>
                        <Button
                          type="button"
                          variant="outline-secondary"
                          onClick={() => {
                            setOrderSearchInput('');
                            setOrderSearch('');
                          }}
                        >
                          Clear
                        </Button>
                      </Form>
                      <Form.Select value={orderStatusFilter} onChange={(event) => setOrderStatusFilter(event.target.value)} style={{ maxWidth: '180px' }}>
                        <option value="all">All Statuses</option>
                        {ORDER_STATUS_OPTIONS.map((status) => (
                          <option key={status.value} value={status.value}>{status.label}</option>
                        ))}
                      </Form.Select>
                      <Form.Select value={orderPaymentFilter} onChange={(event) => setOrderPaymentFilter(event.target.value as 'all' | 'paid' | 'unpaid')} style={{ maxWidth: '160px' }}>
                        <option value="all">All Payments</option>
                        <option value="unpaid">Unpaid</option>
                        <option value="paid">Paid</option>
                      </Form.Select>
                      <Form.Select value={orderSort} onChange={(event) => setOrderSort(event.target.value as 'newest' | 'oldest' | 'totalAmount_desc' | 'totalAmount_asc')} style={{ maxWidth: '180px' }}>
                        <option value="newest">Newest First</option>
                        <option value="oldest">Oldest First</option>
                        <option value="totalAmount_desc">Amount: High to Low</option>
                        <option value="totalAmount_asc">Amount: Low to High</option>
                      </Form.Select>
                    </div>
                    {loadingOrders ? (
                      <Card.Text>Loading orders...</Card.Text>
                    ) : orders.length === 0 ? (
                      <Card.Text>You haven't placed any orders yet.</Card.Text>
                    ) : (
                      <div>
                        {orders.map((order) => (
                          <Card key={order._id} className="mb-2">
                            <Card.Body className="py-2">
                              {(() => {
                                const paymentMeta = getPaymentStatusMeta(order.paymentStatus, order.status);
                                return (
                              <div className="d-flex justify-content-between align-items-center">
                                <div>
                                  <div><strong>{order.orderNumber}</strong></div>
                                  <small>{order.createdAt ? new Date(order.createdAt).toLocaleString() : ''}</small>
                                </div>
                                <div>
                                  <Badge bg={getOrderStatusMeta(order.status).badge} className="me-2">{getOrderStatusMeta(order.status).label}</Badge>
                                  <Badge bg={paymentMeta.badge}>{paymentMeta.label}</Badge>
                                </div>
                              </div>
                                );
                              })()}
                              <div className="mt-1">Total: Rs {Number(order.totalAmount || 0).toFixed(2)}</div>
                              <div className="mt-1 text-muted">
                                <small>
                                  Rider: {order.deliveryBoy?.name?.trim() ? order.deliveryBoy.name : 'Not assigned yet'}
                                  {order.deliveryBoy?.phoneNumber?.trim() ? ` (${order.deliveryBoy.phoneNumber})` : ''}
                                </small>
                              </div>
                              {order.failureReason ? <div className="mt-1 text-danger"><small>Failure: {order.failureReason}</small></div> : null}
                              {order.deliveryNote ? <div className="mt-1 text-muted"><small>Note: {order.deliveryNote}</small></div> : null}
                              <div className="mt-2">
                                <Button size="sm" variant="outline-dark" onClick={() => toggleOrderTimeline(order._id)}>
                                  {expandedOrderId === order._id ? 'Hide Timeline' : 'View Timeline'}
                                </Button>
                                {isDeliveredOrder(order.status) && (
                                  <Button
                                    size="sm"
                                    variant="outline-secondary"
                                    className="ms-2"
                                    onClick={() => requestReturnForOrder(order)}
                                  >
                                    Request Return
                                  </Button>
                                )}
                              </div>
                              {expandedOrderId === order._id ? (
                                <div className="mt-2 border rounded p-2" style={{ background: '#fafafa' }}>
                                  {Array.isArray(order.items) && order.items.length > 0 ? (
                                    <div className="mb-3">
                                      <div className="fw-semibold mb-2">Items</div>
                                      <div className="d-grid gap-2">
                                        {order.items.map((item) => (
                                          <div key={item._id || `${order._id}-${item.productId}`} className="d-flex align-items-center gap-2">
                                            <img
                                              src={resolveProductImage(item.image)}
                                              alt={item.productName}
                                              style={{ width: '52px', height: '52px', objectFit: 'cover', borderRadius: '8px', border: '1px solid #e5e7eb' }}
                                              onError={(event) => {
                                                const target = event.currentTarget;
                                                if (target.src !== getFallbackProductImage()) {
                                                  target.src = getFallbackProductImage();
                                                }
                                              }}
                                            />
                                            <div className="flex-grow-1">
                                              <div className="fw-semibold">{item.productName}</div>
                                              <small className="text-muted">
                                                Qty: {Number(item.quantity || 0)} · Rs {Number(item.price || 0).toFixed(2)} each
                                              </small>
                                            </div>
                                            <div className="text-end">
                                              <small className="text-muted d-block">Subtotal</small>
                                              <strong>Rs {Number(item.subtotal ?? Number(item.price || 0) * Number(item.quantity || 0)).toFixed(2)}</strong>
                                            </div>
                                          </div>
                                        ))}
                                      </div>
                                    </div>
                                  ) : null}

                                  <div className="fw-semibold mb-2">Timeline</div>
                                  {timelineLoadingId === order._id ? (
                                    <small>Loading timeline...</small>
                                  ) : (timelinesByOrderId[order._id] || []).length === 0 ? (
                                    <small className="text-muted">No timeline events available yet.</small>
                                  ) : (
                                    (timelinesByOrderId[order._id] || []).map((event, index) => {
                                      const toMeta = getOrderStatusMeta(event.toStatus);
                                      return (
                                        <div key={`${order._id}-timeline-${index}`} className="mb-1">
                                          <Badge bg={toMeta.badge} className="me-2">{toMeta.label}</Badge>
                                          <small>{new Date(event.createdAt).toLocaleString()}</small>
                                          {event.note ? <div><small className="text-muted">{event.note}</small></div> : null}
                                        </div>
                                      );
                                    })
                                  )}
                                </div>
                              ) : null}
                            </Card.Body>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeSection === 'favourites' && (
                  <div>
                    <Card.Title>Favourites</Card.Title>
                    {favouriteError && <Alert variant="danger">{favouriteError}</Alert>}
                    {loadingFavourites ? (
                      <Card.Text>Loading favourites...</Card.Text>
                    ) : favourites.length === 0 ? (
                      <Card.Text>No favourites yet. Add products from the catalog page.</Card.Text>
                    ) : (
                      <div>
                        {favourites.map((item) => {
                          const image = Array.isArray(item.images) && item.images.length > 0 ? item.images[0] : '';
                          const imageUrl = image && image.startsWith('http') ? image : image ? `${API_ASSET_BASE_URL}${image}` : 'https://via.placeholder.com/120x90?text=Product';
                          const discounted = Number(item.discount ?? 0) > 0;
                          const displayPrice = discounted ? Number(item.price) * (1 - Number(item.discount) / 100) : Number(item.price);

                          return (
                            <Card key={item.productId} className="mb-2">
                              <Card.Body>
                                <div className="d-flex justify-content-between align-items-start gap-3">
                                  <div className="d-flex gap-3">
                                    <img
                                      src={imageUrl}
                                      alt={item.name}
                                      style={{ width: '90px', height: '70px', objectFit: 'cover', borderRadius: '6px', cursor: 'pointer' }}
                                      onClick={() => navigate(`/products/${item.productId}`)}
                                    />
                                    <div>
                                      <div style={{ cursor: 'pointer', fontWeight: 600 }} onClick={() => navigate(`/products/${item.productId}`)}>{item.name}</div>
                                      <small className="text-muted">Rating: {Number(item.ratings ?? 0).toFixed(2)} ({Number(item.reviewsCount ?? 0)} reviews)</small>
                                      <div className="mt-1">
                                        {discounted ? <span style={{ textDecoration: 'line-through' }} className="me-2">Rs {Number(item.price).toFixed(2)}</span> : null}
                                        <strong>Rs {displayPrice.toFixed(2)}</strong>
                                      </div>
                                    </div>
                                  </div>
                                  <Button variant="outline-danger" size="sm" onClick={() => removeFavourite(item.productId)}>
                                    Remove
                                  </Button>
                                </div>
                              </Card.Body>
                            </Card>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}

                {activeSection === 'notifications' && (
                  <div>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <Card.Title className="mb-0">{t('notifications_title')}</Card.Title>
                      <Button
                        variant="outline-dark"
                        size="sm"
                        onClick={markAllNotificationsRead}
                        disabled={unreadNotifications === 0}
                      >
                        {t('notifications_mark_all_read')}
                      </Button>
                    </div>
                    {notificationError && <Alert variant="danger">{notificationError}</Alert>}
                    {loadingNotifications ? (
                      <Card.Text>{t('notifications_loading')}</Card.Text>
                    ) : notifications.length === 0 ? (
                      <Card.Text>{t('notifications_empty')}</Card.Text>
                    ) : (
                      notifications.map((item) => (
                        <Card key={item._id} className="mb-2">
                          <Card.Body className="py-2">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <div className="fw-semibold">{item.title}</div>
                                <small className="text-muted d-block">{item.message}</small>
                                <small className="text-muted">{new Date(item.createdAt).toLocaleString()}</small>
                              </div>
                              <div className="d-flex align-items-center gap-2">
                                {!item.isRead ? <Badge bg="primary">{t('notifications_new')}</Badge> : <Badge bg="secondary">{t('notifications_read')}</Badge>}
                                {!item.isRead ? (
                                  <Button
                                    size="sm"
                                    variant="outline-dark"
                                    onClick={() => markNotificationRead(item._id)}
                                  >
                                    {t('notifications_mark_read')}
                                  </Button>
                                ) : null}
                              </div>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </div>
                )}

                {activeSection === 'returns' && (
                  <div>
                    <Card.Title className="mb-3">Returns</Card.Title>
                    {returnsError && <Alert variant="danger">{returnsError}</Alert>}
                    {returnsSuccess && <Alert variant="success">{returnsSuccess}</Alert>}
                    {loadingReturns ? (
                      <Card.Text>Loading returns...</Card.Text>
                    ) : returnsList.length === 0 ? (
                      <Card.Text>No return requests yet.</Card.Text>
                    ) : (
                      returnsList.map((item) => (
                        <Card key={item._id} className="mb-2">
                          <Card.Body className="py-2">
                            <div className="d-flex justify-content-between align-items-start gap-2">
                              <div>
                                <div className="fw-semibold">{item.orderNumber}</div>
                                <small className="text-muted d-block">Reason: {item.reason}</small>
                                {item.note ? <small className="text-muted d-block">Note: {item.note}</small> : null}
                                {item.adminNote ? <small className="text-muted d-block">Admin: {item.adminNote}</small> : null}
                                {item.refundAmount !== null && item.refundAmount !== undefined ? (
                                  <small className="text-muted d-block">Refund: Rs {Number(item.refundAmount).toFixed(2)}</small>
                                ) : null}
                                <small className="text-muted">{item.createdAt ? new Date(item.createdAt).toLocaleString() : '-'}</small>
                              </div>
                              <Badge bg={item.status === 'requested' ? 'secondary' : item.status === 'approved' ? 'primary' : item.status === 'picked_up' ? 'info' : item.status === 'refunded' ? 'success' : item.status === 'closed' ? 'dark' : 'danger'}>
                                {item.status}
                              </Badge>
                            </div>
                          </Card.Body>
                        </Card>
                      ))
                    )}
                  </div>
                )}
              </Col>
            </Row>
          </Card.Body>
        </Card>
      </Container>
      <Footer />
    </>
  );
}

export default Account;